import React, { useState, useEffect } from 'react';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Calendar from 'lucide-react/dist/esm/icons/calendar.js';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import Hash from 'lucide-react/dist/esm/icons/hash.js';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '../contexts/TranslationContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useGameType } from '../contexts/GameTypeContext';
import api, { apiRoutes } from '../api';
import { getConfig } from '../config/environment';
import { mapWinnersFromApi } from '../utils/winnersApi';
import { getWinnersForGameType } from '../data/drawsApi';
import { getWinnerAmountText, getWinnerMetaItems, getWinnerTitle } from '../utils/winnerDisplay';

const getLabelColor = (winner) => {
  const type = winner.gameType;
  if (type === 'progressive5050' && !winner.secondaryPrize) return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
  if (type === 'chaseTheAce' && !winner.secondaryPrize) return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
  if (type === 'superballBingo' && winner.isJackpot) return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
  if (type === 'prizeDraw') return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
  return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
};

export default function CompactWinnersList({ editMode, onResults }) {
  const navigate = useNavigate();
  const { t } = useTranslations();
  const { language } = useLanguage();
  const { gameType } = useGameType();
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecentWinners();
  }, [gameType]);

  const loadRecentWinners = async () => {
    try {
      const { tlfId } = getConfig();
      const response = await api.get(`${apiRoutes.SiteWinners}?organizationId=${tlfId}`);
      const payload = response?.data?.payload || response?.data;
      const data = payload?.data || payload?.Data || payload || [];
      const mapped = mapWinnersFromApi(data, { gameType }).filter(w => w.amount || w.prize || w.ticketNumber);
      const sliced = mapped.slice(0, 5);
      setWinners(sliced);
      if (typeof onResults === 'function') onResults(sliced.length);
    } catch (error) {
      console.error('Error loading winners:', error);
      const fallback = getWinnersForGameType(gameType || 'classic5050', 5).map(w => ({
        id: w.id,
        name: w.name,
        amount: w.amount,
        prize: w.prize || null,
        date: w.date,
        draw: w.draw,
        isWinner: w.isWinner !== false,
        gameType: w.gameType || gameType
      }));
      setWinners(fallback);
      if (typeof onResults === 'function') onResults(fallback.length);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          <h3 className="text-sm font-bold text-gray-900 dark:text-white">
            {t('compactWinners.title', 'Recent Winners')}
          </h3>
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  if (winners.length === 0) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg p-4 border ${editMode ? 'border-dashed border-gray-300 dark:border-gray-600' : 'border-gray-100 dark:border-gray-700'}`}>
        <div className="flex items-center gap-2 mb-2">
          <Trophy className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          <h3 className="text-sm font-bold text-gray-900 dark:text-white">
            {t('compactWinners.title', 'Recent Winners')}
          </h3>
        </div>
        <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
          {t('compactWinners.noWinnersYet', 'No winners yet — check back after the next draw!')}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800/90 rounded-2xl p-3.5 sm:p-4 border-0 sm:border border-gray-100 dark:border-gray-700/50 shadow-lg">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        <h3 className="text-sm font-bold text-gray-900 dark:text-white">
          {t('compactWinners.title', 'Recent Winners')}
        </h3>
      </div>

      <div className="space-y-2">
        {winners.map((winner) => {
          const label = getWinnerTitle(winner, t);
          const colorClass = getLabelColor(winner);
          const amountText = getWinnerAmountText(winner, { language });
          const metaItems = getWinnerMetaItems(winner, t)
            .filter(item => item.label !== 'Date' && item.label !== 'Ticket')
            .slice(0, 3);
          return (
            <div
              key={winner.id}
              className="flex items-center justify-between text-sm py-2 border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 dark:text-white truncate">
                  {winner.name}
                  {winner.gameType === 'squareGame' && winner.squareNumber && (
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      <Hash className="w-3 h-3 inline -mt-0.5" />{winner.squareNumber}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-0.5">
                  <Calendar className="w-3 h-3" />
                  {winner.date}
                  {label && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none ${colorClass}`}>
                      {label}
                    </span>
                  )}
                </div>
                {metaItems.length > 0 && (
                  <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
                    {metaItems.map(item => `${item.label}: ${item.value}`).join(' · ')}
                  </div>
                )}
                {winner.gameType === 'prizeDraw' && winner.prize && (
                  <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
                    {winner.prize}
                  </div>
                )}
                {winner.gameType === 'chaseTheAce' && winner.cardDrawn && (
                  <div className={`text-[10px] mt-0.5 ${
                    ['♥', '♦'].some(s => winner.cardDrawn.includes(s))
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}>
                    🃏 {winner.cardDrawn}
                  </div>
                )}
              </div>
              <div className="max-w-[6rem] text-right text-green-600 dark:text-green-400 font-bold text-sm truncate">
                {amountText}
              </div>
            </div>
          );
        })}
      </div>

      <button
        onClick={(e) => {
          if (editMode) {
            e.preventDefault();
            return;
          }
          navigate('/winners');
        }}
        className={`mt-3 w-full flex items-center justify-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium py-2 rounded hover:bg-blue-50 dark:hover:bg-gray-700 transition-all ${editMode ? 'cursor-default' : ''}`}
      >
        {t('compactWinners.viewAll', 'View All Winners')}
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}
